<template>
    <div :class="$style.container">
        <!-- 통계 탭 네비게이션 -->
        <StatsTabs />

        <!-- 로딩 상태 -->
        <div v-if="loading">
            <LoadingScreen loading-text="유입 경로 통계를 불러오는 중..." />
        </div>

        <!-- 에러 상태 -->
        <div v-else-if="error" :class="$style.error">
            <p>{{ error }}</p>
            <button @click="loadTrafficStats" :class="$style.retryButton">다시 시도</button>
        </div>

        <!-- 통계 데이터 -->
        <div v-else>
            <div :class="$style.header">
                <h1 :class="$style.title">🚀 유입 경로 통계</h1>
                <p :class="$style.subtitle">사이트 방문자의 유입 경로, UTM 캠페인, 랜딩 페이지 분석</p>
            </div>

            <!-- 필터 바 -->
            <div :class="$style.filterBar">
                <!-- 사용자 유형 필터 -->
                <div :class="$style.userTypeFilter">
                    <button :class="[$style.filterBtn, { [$style.activeFilter]: userTypeFilter === 'all' }]"
                        @click="setUserTypeFilter('all')">
                        전체
                        <span :class="$style.filterCount">{{ rawStats?.total || 0 }}</span>
                    </button>
                    <button
                        :class="[$style.filterBtn, $style.loginFilter, { [$style.activeFilter]: userTypeFilter === 'login' }]"
                        @click="setUserTypeFilter('login')">
                        로그인
                        <span :class="$style.filterCount">{{ rawStats?.byUserType?.loginUser || 0 }}</span>
                    </button>
                    <button
                        :class="[$style.filterBtn, $style.guestFilter, { [$style.activeFilter]: userTypeFilter === 'guest' }]"
                        @click="setUserTypeFilter('guest')">
                        비로그인
                        <span :class="$style.filterCount">{{ rawStats?.byUserType?.unknown || 0 }}</span>
                    </button>
                </div>

                <!-- 기간 선택 -->
                <div :class="$style.periodSelector">
                    <label>조회 기간:</label>
                    <select v-model="periodMode" @change="onPeriodModeChange" :class="$style.periodSelect">
                        <option value="1">오늘</option>
                        <option value="7">최근 7일</option>
                        <option value="14">최근 14일</option>
                        <option value="30">최근 30일</option>
                        <option value="60">최근 60일</option>
                        <option value="90">최근 90일</option>
                        <option value="custom">직접 선택</option>
                    </select>
                </div>

                <!-- 달력 선택 (직접 선택 모드일 때) -->
                <div v-if="periodMode === 'custom'" :class="$style.datePickerWrapper">
                    <div :class="$style.dateInputGroup">
                        <label>시작일:</label>
                        <input type="date" v-model="customStartDate" :max="customEndDate || todayStr"
                            :class="$style.dateInput" />
                    </div>
                    <span :class="$style.dateSeparator">~</span>
                    <div :class="$style.dateInputGroup">
                        <label>종료일:</label>
                        <input type="date" v-model="customEndDate" :min="customStartDate" :max="todayStr"
                            :class="$style.dateInput" />
                    </div>
                    <button @click="loadTrafficStats" :class="$style.applyButton">
                        조회
                    </button>
                </div>

                <button @click="loadTrafficStats" :class="$style.refreshButton">
                    🔄 새로고침
                </button>
            </div>

            <!-- 요약 카드 -->
            <div :class="$style.summary">
                <div :class="$style.summaryCard">
                    <h3>{{ userTypeFilterLabel }} 방문 수</h3>
                    <p :class="$style.summaryNumber">{{ trafficStats?.total || 0 }}</p>
                </div>
                <div :class="$style.summaryCard">
                    <h3>직접 접속</h3>
                    <p :class="$style.summaryNumber">{{ trafficStats?.byCategory?.['직접 접속'] || 0 }}</p>
                </div>
                <div :class="$style.summaryCard">
                    <h3>검색 엔진</h3>
                    <p :class="$style.summaryNumber">{{ trafficStats?.byCategory?.['검색 엔진'] || 0 }}</p>
                </div>
                <div :class="$style.summaryCard">
                    <h3>SNS</h3>
                    <p :class="$style.summaryNumber">{{ trafficStats?.byCategory?.['SNS'] || 0 }}</p>
                </div>
                <div :class="$style.summaryCard">
                    <h3>캠페인 (UTM)</h3>
                    <p :class="$style.summaryNumber">{{ trafficStats?.byCategory?.['캠페인'] || 0 }}</p>
                </div>
            </div>


            <!-- 상세 통계 그리드 -->
            <div :class="$style.statsGrid">
                <!-- 카테고리별 -->
                <div :class="$style.statsCard">
                    <h4>📂 유입 경로 카테고리</h4>
                    <div :class="$style.statsList">
                        <div v-for="(count, category) in trafficStats?.byCategory" :key="category"
                            :class="$style.statsItem">
                            <span :class="$style.statsLabel">{{ category }}</span>
                            <div :class="$style.statsBarWrapper">
                                <div :class="$style.statsBar"
                                    :style="{ width: `${(count / trafficStats.total) * 100}%` }"></div>
                            </div>
                            <span :class="$style.statsValue">{{ count }}</span>
                            <span :class="$style.statsPercent">{{ Math.round((count / trafficStats.total) * 100)
                            }}%</span>
                        </div>
                    </div>
                </div>

                <!-- 소스별 -->
                <div :class="$style.statsCard">
                    <h4>🔗 소스별 (상위 15개)</h4>
                    <div :class="$style.statsList">
                        <div v-for="(count, source) in topSources" :key="source" :class="$style.statsItem">
                            <span :class="$style.statsLabel">{{ source }}</span>
                            <span :class="$style.statsValue">{{ count }}회</span>
                        </div>
                        <div v-if="Object.keys(topSources).length === 0" :class="$style.emptyState">
                            데이터가 없습니다
                        </div>
                    </div>
                </div>

                <!-- 기기별 -->
                <div :class="$style.statsCard">
                    <h4>📱 기기별</h4>
                    <div :class="$style.statsList">
                        <div v-for="(count, device) in trafficStats?.byDevice" :key="device" :class="$style.statsItem">
                            <span :class="$style.statsLabel">{{ device }}</span>
                            <div :class="$style.statsBarWrapper">
                                <div :class="$style.statsBar"
                                    :style="{ width: `${(count / trafficStats.total) * 100}%` }"></div>
                            </div>
                            <span :class="$style.statsValue">{{ count }}</span>
                            <span :class="$style.statsPercent">{{ Math.round((count / trafficStats.total) * 100)
                            }}%</span>
                        </div>
                    </div>
                </div>

                <!-- 브라우저별 -->
                <div :class="$style.statsCard">
                    <h4>🌐 브라우저별</h4>
                    <div :class="$style.statsList">
                        <div v-for="(count, browser) in trafficStats?.byBrowser" :key="browser"
                            :class="$style.statsItem">
                            <span :class="$style.statsLabel">{{ browser }}</span>
                            <span :class="$style.statsValue">{{ count }}회</span>
                        </div>
                    </div>
                </div>

                <!-- 캠페인별 (UTM) -->
                <div :class="$style.statsCard">
                    <h4>📣 캠페인별 (UTM)</h4>
                    <div :class="$style.statsList">
                        <div v-for="(count, campaign) in trafficStats?.byCampaign" :key="campaign"
                            :class="$style.statsItem">
                            <span :class="$style.statsLabel">{{ campaign }}</span>
                            <span :class="$style.statsValue">{{ count }}회</span>
                        </div>
                        <div v-if="Object.keys(trafficStats?.byCampaign || {}).length === 0" :class="$style.emptyState">
                            UTM 캠페인 데이터가 없습니다
                        </div>
                    </div>
                </div>

                <!-- 랜딩 페이지별 -->
                <div :class="$style.statsCardWide">
                    <h4>📄 랜딩 페이지별 (상위 15개)</h4>
                    <div :class="$style.statsList">
                        <div v-for="(count, page) in topLandingPages" :key="page" :class="$style.statsItem">
                            <span :class="$style.statsLabel">{{ page }}</span>
                            <span :class="$style.statsValue">{{ count }}회</span>
                        </div>
                        <div v-if="Object.keys(topLandingPages).length === 0" :class="$style.emptyState">
                            데이터가 없습니다
                        </div>
                    </div>
                </div>

                <!-- 일별 추이 -->
                <div :class="$style.timelineSection">
                    <h3 :class="$style.sectionTitle">📈 일별 방문 추이</h3>
                    <div :class="$style.timeline">
                        <div v-for="(count, date) in sortedTimeline" :key="date" :class="$style.timelineItem">
                            <span :class="$style.timelineDate">{{ formatDate(date) }}</span>
                            <div :class="$style.timelineBarWrapper">
                                <div :class="$style.timelineBar"
                                    :style="{ width: `${(count / maxTimelineCount) * 100}%` }">
                                </div>
                            </div>
                            <span :class="$style.timelineCount">{{ count }}</span>
                        </div>
                    </div>
                </div>
            </div>


            <!-- 활동 통계 섹션 -->
            <div :class="$style.activitySection">
                <h2 :class="$style.activitySectionTitle">📊 사용자 활동 통계 (익명)</h2>
                <p :class="$style.activitySubtitle">사용자를 특정할 수 없는 집계 통계입니다</p>

                <!-- 활동 사용자 유형 필터 -->
                <div :class="$style.activityFilterBar">
                    <div :class="$style.userTypeFilter">
                        <button :class="[$style.filterBtn, { [$style.activeFilter]: activityUserFilter === 'all' }]"
                            @click="setActivityUserFilter('all')">
                            전체
                            <span :class="$style.filterCount">{{ activityStats?.total || 0 }}</span>
                        </button>
                        <button
                            :class="[$style.filterBtn, $style.loginFilter, { [$style.activeFilter]: activityUserFilter === 'login' }]"
                            @click="setActivityUserFilter('login')">
                            로그인
                            <span :class="$style.filterCount">{{ activityStats?.loginUserCount || 0 }}</span>
                        </button>
                        <button
                            :class="[$style.filterBtn, $style.guestFilter, { [$style.activeFilter]: activityUserFilter === 'guest' }]"
                            @click="setActivityUserFilter('guest')">
                            비로그인
                            <span :class="$style.filterCount">{{ activityStats?.anonymousCount || 0 }}</span>
                        </button>
                    </div>
                </div>

                <div v-if="activityLoading" :class="$style.activityLoading">
                    활동 통계를 불러오는 중...
                </div>

                <div v-else-if="filteredActivityStats">
                    <!-- 활동 요약 카드 -->
                    <div :class="$style.activitySummary">
                        <div :class="$style.activitySummaryCard">
                            <h3>{{ activityUserFilterLabel }} 활동 수</h3>
                            <p :class="$style.activityNumber">{{ filteredActivityStats.total || 0 }}</p>
                        </div>
                    </div>

                    <!-- 활동 통계 그리드 -->
                    <div :class="$style.activityGrid">
                        <!-- 활동 유형별 -->
                        <div :class="$style.activityCard">
                            <h4>🎯 활동 유형별</h4>
                            <div :class="$style.activityList">
                                <div v-for="(count, type) in sortedActivityTypes" :key="type"
                                    :class="$style.activityItem">
                                    <span :class="$style.activityLabel">{{ formatActivityType(type) }}</span>
                                    <div :class="$style.activityBarWrapper">
                                        <div :class="$style.activityBar"
                                            :style="{ width: `${(count / filteredActivityStats.total) * 100}%` }"></div>
                                    </div>
                                    <span :class="$style.activityValue">{{ count }}</span>
                                </div>
                                <div v-if="Object.keys(filteredActivityStats.byType || {}).length === 0"
                                    :class="$style.emptyState">
                                    데이터가 없습니다
                                </div>
                            </div>
                        </div>

                        <!-- 대상별 -->
                        <div :class="$style.activityCard">
                            <h4>📌 대상별</h4>
                            <div :class="$style.activityList">
                                <div v-for="(count, target) in sortedTargets" :key="target"
                                    :class="$style.activityItem">
                                    <span :class="$style.activityLabel">{{ target }}</span>
                                    <span :class="$style.activityValue">{{ count }}회</span>
                                </div>
                                <div v-if="Object.keys(filteredActivityStats.byTarget || {}).length === 0"
                                    :class="$style.emptyState">
                                    데이터가 없습니다
                                </div>
                            </div>
                        </div>

                        <!-- 기기별 (비로그인만) -->
                        <div v-if="activityUserFilter !== 'login'" :class="$style.activityCard">
                            <h4>📱 기기별 (비로그인)</h4>
                            <div :class="$style.activityList">
                                <div v-for="(count, device) in filteredActivityStats.byDevice" :key="device"
                                    :class="$style.activityItem">
                                    <span :class="$style.activityLabel">{{ device }}</span>
                                    <span :class="$style.activityValue">{{ count }}회</span>
                                </div>
                                <div v-if="Object.keys(filteredActivityStats.byDevice || {}).length === 0"
                                    :class="$style.emptyState">
                                    데이터가 없습니다
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- 활동 일별 추이 -->
                    <div :class="$style.activityTimelineSection">
                        <h4>📈 일별 활동 추이</h4>
                        <div :class="$style.activityTimeline">
                            <div v-for="(count, date) in sortedActivityTimeline" :key="date"
                                :class="$style.timelineItem">
                                <span :class="$style.timelineDate">{{ formatDate(date) }}</span>
                                <div :class="$style.timelineBarWrapper">
                                    <div :class="[$style.timelineBar, $style.activityTimelineBar]"
                                        :style="{ width: `${(count / maxActivityTimelineCount) * 100}%` }"></div>
                                </div>
                                <span :class="$style.timelineCount">{{ count }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import StatsTabs from "@/components/StatsTabs.vue";
import LoadingScreen from "@/components/LoadingScreen.vue";
import { useUserStore } from "@/store/user";
import { getTrafficStats } from "@/utils/activityTracker";
import { getAllActivityStats } from "@/utils/read/user";

export default {
    name: "TrafficStats",
    components: {
        StatsTabs,
        LoadingScreen,
    },
    data() {
        return {
            loading: false,
            error: null,
            periodMode: "30", // 기본값: 최근 30일
            customStartDate: "",
            customEndDate: "",
            userTypeFilter: "all", // all, login, guest
            rawData: [], // 원본 데이터
            rawStats: null, // 원본 통계 (전체)
            // 활동 통계
            activityStats: null,
            activityLoading: false,
            activityUserFilter: "all", // all, login, guest
            // 활동 원본 데이터 (필터링용)
            activityRawData: {
                loginData: [],
                anonymousData: [],
            },
        };
    },
    computed: {
        // 오늘 날짜 문자열 (YYYY-MM-DD)
        todayStr() {
            const today = new Date();
            return today.toISOString().split("T")[0];
        },

        // 필터된 데이터
        filteredData() {
            if (!this.rawData) return [];
            if (this.userTypeFilter === "all") return this.rawData;
            if (this.userTypeFilter === "login") {
                return this.rawData.filter(item => item.userId);
            }
            if (this.userTypeFilter === "guest") {
                return this.rawData.filter(item => !item.userId);
            }
            return this.rawData;
        },

        // 필터된 데이터 기반 통계
        trafficStats() {
            const data = this.filteredData;

            // 백엔드에서 보낸 통계를 기반으로 시작
            const baseStats = this.rawStats || {
                total: 0,
                bySource: {},
                byCategory: {},
                byDevice: {},
                byBrowser: {},
                byCampaign: {},
                byLandingPage: {},
                timeline: {},
                byUserType: {
                    loginUser: 0,
                    unknown: 0,
                },
            };

            // 필터링이 "all"이면 백엔드 통계를 그대로 사용
            if (this.userTypeFilter === "all") {
                return {
                    total: baseStats.total,
                    bySource: baseStats.bySource || {},
                    byCategory: baseStats.byCategory || {},
                    byDevice: baseStats.byDevice || {},
                    byBrowser: baseStats.byBrowser || {},
                    byCampaign: baseStats.byCampaign || {},
                    byLandingPage: baseStats.byLandingPage || {},
                    timeline: baseStats.timeline || {},
                };
            }

            // 필터링이 필요한 경우 클라이언트에서 재계산
            if (!data || data.length === 0) {
                return {
                    total: 0,
                    bySource: {},
                    byCategory: {},
                    byDevice: {},
                    byBrowser: {},
                    byCampaign: {},
                    byLandingPage: {},
                    timeline: {},
                };
            }

            const stats = {
                total: data.length,
                bySource: {},
                byCategory: {},
                byDevice: {},
                byBrowser: {},
                byCampaign: {},
                byLandingPage: {},
                timeline: {},
            };

            data.forEach(item => {
                // 소스별
                const source = item.source || "unknown";
                stats.bySource[source] = (stats.bySource[source] || 0) + 1;

                // 카테고리별
                const category = item.category || "알 수 없음";
                stats.byCategory[category] = (stats.byCategory[category] || 0) + 1;

                // 기기별
                const device = item.device || "Unknown";
                stats.byDevice[device] = (stats.byDevice[device] || 0) + 1;

                // 브라우저별
                const browser = item.browser || "Unknown";
                stats.byBrowser[browser] = (stats.byBrowser[browser] || 0) + 1;

                // 캠페인별 (UTM이 있는 경우)
                if (item.campaign && item.campaign !== null && item.campaign !== 'null') {
                    stats.byCampaign[item.campaign] = (stats.byCampaign[item.campaign] || 0) + 1;
                }

                // 랜딩 페이지별
                const landingPage = item.landingPage || "/";
                stats.byLandingPage[landingPage] = (stats.byLandingPage[landingPage] || 0) + 1;

                // 일별 타임라인 (ISO 문자열로 받은 경우)
                if (item.timestamp) {
                    let date;
                    if (typeof item.timestamp === 'string') {
                        // ISO 문자열인 경우
                        date = new Date(item.timestamp);
                    } else if (item.timestamp?.toDate && typeof item.timestamp.toDate === 'function') {
                        // Firestore Timestamp 객체인 경우
                        date = item.timestamp.toDate();
                    } else if (item.timestamp instanceof Date) {
                        date = item.timestamp;
                    }

                    if (date && !isNaN(date.getTime())) {
                        const dateStr = date.toISOString().split("T")[0];
                        stats.timeline[dateStr] = (stats.timeline[dateStr] || 0) + 1;
                    }
                }
            });

            return stats;
        },

        // 필터 라벨
        userTypeFilterLabel() {
            const labels = {
                all: "전체",
                login: "로그인 사용자",
                guest: "비로그인 사용자",
            };
            return labels[this.userTypeFilter] || "전체";
        },

        // 상위 15개 소스
        topSources() {
            if (!this.trafficStats?.bySource) return {};
            const entries = Object.entries(this.trafficStats.bySource);
            entries.sort((a, b) => b[1] - a[1]);
            return Object.fromEntries(entries.slice(0, 15));
        },

        // 상위 15개 랜딩 페이지
        topLandingPages() {
            if (!this.trafficStats?.byLandingPage) return {};
            const entries = Object.entries(this.trafficStats.byLandingPage);
            entries.sort((a, b) => b[1] - a[1]);
            return Object.fromEntries(entries.slice(0, 15));
        },

        // 정렬된 타임라인 (날짜순)
        sortedTimeline() {
            if (!this.trafficStats?.timeline) return {};
            const entries = Object.entries(this.trafficStats.timeline);
            entries.sort((a, b) => a[0].localeCompare(b[0]));
            return Object.fromEntries(entries);
        },

        // 타임라인 최대값 (바 차트용)
        maxTimelineCount() {
            if (!this.trafficStats?.timeline) return 1;
            const values = Object.values(this.trafficStats.timeline);
            return Math.max(...values, 1);
        },

        // 활동 사용자 필터 라벨
        activityUserFilterLabel() {
            const labels = {
                all: "전체",
                login: "로그인 사용자",
                guest: "비로그인 사용자",
            };
            return labels[this.activityUserFilter] || "전체";
        },

        // 필터된 활동 통계
        filteredActivityStats() {
            if (!this.activityStats) {
                return {
                    total: 0,
                    byType: {},
                    byDevice: {},
                    byBrowser: {},
                    byTarget: {},
                    timeline: {},
                };
            }

            // 전체인 경우 원본 반환
            if (this.activityUserFilter === "all") {
                return this.activityStats;
            }

            // 로그인만
            if (this.activityUserFilter === "login") {
                return {
                    total: this.activityStats.loginUserCount || 0,
                    byType: this.activityStats.loginByType || {},
                    byDevice: {},
                    byBrowser: {},
                    byTarget: this.activityStats.loginByTarget || {},
                    timeline: this.activityStats.loginTimeline || {},
                };
            }

            // 비로그인만
            if (this.activityUserFilter === "guest") {
                return {
                    total: this.activityStats.anonymousCount || 0,
                    byType: this.activityStats.anonymousByType || {},
                    byDevice: this.activityStats.byDevice || {},
                    byBrowser: this.activityStats.byBrowser || {},
                    byTarget: this.activityStats.anonymousByTarget || {},
                    timeline: this.activityStats.anonymousTimeline || {},
                };
            }

            return this.activityStats;
        },

        // 정렬된 활동 유형
        sortedActivityTypes() {
            if (!this.filteredActivityStats?.byType) return {};
            const entries = Object.entries(this.filteredActivityStats.byType);
            entries.sort((a, b) => b[1] - a[1]);
            return Object.fromEntries(entries);
        },

        // 정렬된 대상
        sortedTargets() {
            if (!this.filteredActivityStats?.byTarget) return {};
            const entries = Object.entries(this.filteredActivityStats.byTarget);
            entries.sort((a, b) => b[1] - a[1]);
            return Object.fromEntries(entries.slice(0, 15));
        },

        // 정렬된 활동 타임라인
        sortedActivityTimeline() {
            if (!this.filteredActivityStats?.timeline) return {};
            const entries = Object.entries(this.filteredActivityStats.timeline);
            entries.sort((a, b) => a[0].localeCompare(b[0]));
            return Object.fromEntries(entries);
        },

        // 활동 타임라인 최대값
        maxActivityTimelineCount() {
            if (!this.filteredActivityStats?.timeline) return 1;
            const values = Object.values(this.filteredActivityStats.timeline);
            return Math.max(...values, 1);
        },
    },
    async mounted() {
        // 접근 권한 체크
        const userStore = useUserStore();
        if (userStore.auth !== 'admin') {
            this.$alert("접근 권한이 없습니다.");
            this.$router.push("/");
            return;
        }

        await Promise.all([
            this.loadTrafficStats(),
            this.loadActivityStats(),
        ]);
    },
    methods: {
        onPeriodModeChange() {
            if (this.periodMode === "custom") {
                // 직접 선택 모드로 전환 시 기본값 설정
                const today = new Date();
                const weekAgo = new Date();
                weekAgo.setDate(today.getDate() - 7);
                this.customEndDate = today.toISOString().split("T")[0];
                this.customStartDate = weekAgo.toISOString().split("T")[0];
            } else {
                // 프리셋 모드일 때 바로 로드
                this.loadTrafficStats();
            }
        },

        async loadTrafficStats() {
            this.loading = true;
            this.error = null;
            try {
                let result;
                if (this.periodMode === "custom") {
                    // 직접 선택 모드
                    if (!this.customStartDate || !this.customEndDate) {
                        this.error = "시작일과 종료일을 선택해주세요.";
                        this.loading = false;
                        return;
                    }
                    result = await getTrafficStats({
                        startDate: this.customStartDate,
                        endDate: this.customEndDate,
                    });
                } else {
                    // 프리셋 모드
                    const days = parseInt(this.periodMode, 10);
                    result = await getTrafficStats(days);
                }
                this.rawData = result.data;
                this.rawStats = result.stats;

                // 활동 통계도 같이 로드
                await this.loadActivityStats();
            } catch (error) {
                console.error("유입 경로 통계 로드 오류:", error);
                this.error = "유입 경로 통계를 불러오는 중 오류가 발생했습니다.";
            } finally {
                this.loading = false;
            }
        },

        setUserTypeFilter(filter) {
            this.userTypeFilter = filter;
        },

        setActivityUserFilter(filter) {
            this.activityUserFilter = filter;
        },

        async loadActivityStats() {
            this.activityLoading = true;
            try {
                let result;
                if (this.periodMode === "custom") {
                    if (!this.customStartDate || !this.customEndDate) {
                        return;
                    }
                    result = await getAllActivityStats({
                        startDate: this.customStartDate,
                        endDate: this.customEndDate,
                    });
                } else {
                    const days = parseInt(this.periodMode, 10);
                    result = await getAllActivityStats(days);
                }
                // read/user.js의 getAllActivityStats는 stats 객체를 직접 반환
                this.activityStats = result;
            } catch (error) {
                console.error("활동 통계 로드 오류:", error);
            } finally {
                this.activityLoading = false;
            }
        },

        formatActivityType(type) {
            const labels = {
                PAGE_VIEW: "HOME_VIEW",
            };
            return labels[type] ?? type;
        },

        formatDate(dateStr) {
            const date = new Date(dateStr);
            return `${date.getMonth() + 1}/${date.getDate()}`;
        },

    },
};
</script>

<style module>
/* ========================================
   반응형 디자인 (최대 1024px)
   - Medium: 481px ~ (기본 스타일)
   - Small: ~ 480px
   ======================================== */

.container {
    width: 100%;
    max-width: 1024px;
    margin: 0 auto;
    padding: 0px 15px;
}

.header {
    margin-bottom: 20px;
}

.title {
    font-size: 1.5rem;
    margin: 0 0 6px 0;
    color: #333;
}

.subtitle {
    color: #666;
    margin: 0;
    font-size: 0.85rem;
}

.error {
    text-align: center;
    padding: 40px;
    color: #d32f2f;
}

.retryButton {
    margin-top: 15px;
    padding: 10px 20px;
    background: #2196f3;
    color: white;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    font-size: 0.9rem;
}

.retryButton:hover {
    background: #1976d2;
}

/* 필터 바 */
.filterBar {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    margin-bottom: 25px;
    gap: 15px;
    flex-wrap: wrap;
}

/* 사용자 유형 필터 */
.userTypeFilter {
    display: flex;
    gap: 4px;
    background: #f0f0f0;
    padding: 4px;
    border-radius: 10px;
}

.filterBtn {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 14px;
    border: none;
    border-radius: 8px;
    background: transparent;
    color: #666;
    font-size: 0.85rem;
    cursor: pointer;
    transition: all 0.2s ease;
}

.filterBtn:hover {
    background: rgba(0, 0, 0, 0.05);
}

.filterBtn.activeFilter {
    background: white;
    color: #333;
    font-weight: 600;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.filterBtn.loginFilter.activeFilter {
    color: #2196f3;
}

.filterBtn.guestFilter.activeFilter {
    color: #ff9800;
}

.filterCount {
    background: rgba(0, 0, 0, 0.08);
    padding: 2px 6px;
    border-radius: 10px;
    font-size: 0.75rem;
    font-weight: 600;
}

.filterBtn.activeFilter .filterCount {
    background: rgba(0, 0, 0, 0.1);
}

.filterBtn.loginFilter.activeFilter .filterCount {
    background: rgba(33, 150, 243, 0.15);
    color: #2196f3;
}

.filterBtn.guestFilter.activeFilter .filterCount {
    background: rgba(255, 152, 0, 0.15);
    color: #ff9800;
}

.periodSelector {
    display: flex;
    align-items: center;
    gap: 10px;
}

.periodSelector label {
    font-size: 0.9rem;
    color: #555;
    font-weight: 500;
}

.periodSelect {
    padding: 10px 15px;
    border: 1px solid #ddd;
    border-radius: 8px;
    font-size: 0.9rem;
    background: white;
    cursor: pointer;
    min-width: 140px;
}

.refreshButton {
    padding: 10px 20px;
    background: #4caf50;
    color: white;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-size: 0.9rem;
    transition: background 0.2s;
}

.refreshButton:hover {
    background: #43a047;
}

/* 달력 선택 */
.datePickerWrapper {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
}

.dateInputGroup {
    display: flex;
    align-items: center;
    gap: 6px;
}

.dateInputGroup label {
    font-size: 0.85rem;
    color: #555;
    white-space: nowrap;
}

.dateInput {
    padding: 8px 12px;
    border: 1px solid #ddd;
    border-radius: 8px;
    font-size: 0.9rem;
    background: white;
    cursor: pointer;
}

.dateInput:focus {
    outline: none;
    border-color: #4caf50;
    box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.15);
}

.dateSeparator {
    color: #888;
    font-size: 0.9rem;
}

.applyButton {
    padding: 8px 16px;
    background: #2196f3;
    color: white;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-size: 0.85rem;
    transition: background 0.2s;
}

.applyButton:hover {
    background: #1976d2;
}

/* 요약 카드 */
.summary {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
    gap: 15px;
    margin-bottom: 30px;
}

.summaryCard {
    background: white;
    padding: 15px;
    border-radius: 10px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    text-align: center;
    border: 1px solid #e8e8e8;
}

.summaryCard h3 {
    margin: 0 0 8px 0;
    font-size: 0.8rem;
    color: #666;
    font-weight: 600;
}

.summaryNumber {
    font-size: 1.5rem;
    font-weight: bold;
    color: #4caf50;
    margin: 0;
}


/* 통계 그리드 */
.statsGrid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 15px;
    margin-bottom: 20px;
}

.statsCard {
    background: white;
    padding: 15px;
    border-radius: 10px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    border: 1px solid #e8e8e8;
    min-width: 100%;
}

.statsCardWide {
    background: white;
    padding: 15px;
    border-radius: 10px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    border: 1px solid #e8e8e8;
    grid-column: span 1;
    min-width: 100%;
}

.statsCard h4,
.statsCardWide h4 {
    margin: 0 0 15px 0;
    font-size: 0.95rem;
    color: #333;
    font-weight: 600;
}

.statsList {
    display: flex;
    flex-direction: column;
    gap: 10px;
    max-height: 250px;
    overflow-y: auto;
}

.statsItem {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 10px;
    background: #f8f9fa;
    border-radius: 8px;
    font-size: 0.85rem;
}

.statsLabel {
    flex: 1;
    color: #333;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 80px;
}

.statsBarWrapper {
    flex: 2;
    height: 8px;
    background: #e9ecef;
    border-radius: 4px;
    overflow: hidden;
}

.statsBar {
    height: 100%;
    background: linear-gradient(90deg, #4caf50, #81c784);
    border-radius: 4px;
    transition: width 0.3s ease;
}

.statsBar.blueBar {
    background: linear-gradient(90deg, #2196f3, #64b5f6);
}

.statsBar.orangeBar {
    background: linear-gradient(90deg, #ff9800, #ffb74d);
}

.statsValue {
    color: #4caf50;
    font-weight: 600;
    flex-shrink: 0;
    min-width: 50px;
    text-align: right;
}

.statsPercent {
    color: #888;
    font-size: 0.8rem;
    flex-shrink: 0;
    min-width: 40px;
    text-align: right;
}

.emptyState {
    text-align: center;
    color: #999;
    padding: 20px;
    font-size: 0.85rem;
}

/* 타임라인 섹션 */
.timelineSection {
    background: white;
    padding: 15px;
    border-radius: 10px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    border: 1px solid #e8e8e8;
}

.sectionTitle {
    margin: 0 0 15px 0;
    font-size: 0.95rem;
    color: #333;
}

.timeline {
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-height: 600px;
    overflow-y: auto;
}

.timelineItem {
    display: flex;
    align-items: center;
    gap: 12px;
}

.timelineDate {
    flex-shrink: 0;
    width: 50px;
    font-size: 0.8rem;
    color: #666;
}

.timelineBarWrapper {
    flex: 1;
    height: 20px;
    background: #f0f0f0;
    border-radius: 4px;
    overflow: hidden;
}

.timelineBar {
    height: 100%;
    background: linear-gradient(90deg, #2196f3, #64b5f6);
    border-radius: 4px;
    transition: width 0.3s ease;
}

.timelineCount {
    flex-shrink: 0;
    width: 40px;
    text-align: right;
    font-size: 0.85rem;
    font-weight: 600;
    color: #2196f3;
}

/* 활동 통계 섹션 */
.activitySection {
    margin-top: 30px;
    padding-top: 20px;
    border-top: 2px solid #e8e8e8;
}

.activitySectionTitle {
    font-size: 1.2rem;
    margin: 0 0 6px 0;
    color: #333;
}

.activitySubtitle {
    color: #888;
    margin: 0 0 15px 0;
    font-size: 0.8rem;
}

.activityFilterBar {
    margin-bottom: 20px;
}

.activityLoading {
    text-align: center;
    padding: 40px;
    color: #666;
    font-size: 0.9rem;
}

.activitySummary {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 10px;
    margin-bottom: 20px;
}

.activitySummaryCard {
    background: white;
    padding: 15px;
    border-radius: 10px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    text-align: center;
    border: 1px solid #e8e8e8;
}

.activitySummaryCard h3 {
    margin: 0 0 8px 0;
    font-size: 0.75rem;
    color: #666;
    font-weight: 600;
}

.activityNumber {
    font-size: 1.4rem;
    font-weight: bold;
    color: #9c27b0;
    margin: 0;
}

.blueText {
    color: #2196f3;
}

.orangeText {
    color: #ff9800;
}

.loginCard {
    border-left: 3px solid #2196f3;
}

.guestCard {
    border-left: 3px solid #ff9800;
}

.activityGrid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 15px;
    margin-bottom: 20px;
}

.activityCard {
    background: white;
    padding: 15px;
    border-radius: 10px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    border: 1px solid #e8e8e8;
}

.activityCard h4 {
    margin: 0 0 12px 0;
    font-size: 0.9rem;
    color: #333;
    font-weight: 600;
}

.activityList {
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-height: 300px;
    overflow-y: auto;
}

.activityItem {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 10px;
    background: #f8f9fa;
    border-radius: 8px;
    font-size: 0.8rem;
}

.activityLabel {
    flex: 1;
    color: #333;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 80px;
}

.activityBarWrapper {
    flex: 2;
    height: 6px;
    background: #e9ecef;
    border-radius: 3px;
    overflow: hidden;
}

.activityBar {
    height: 100%;
    background: linear-gradient(90deg, #9c27b0, #ba68c8);
    border-radius: 3px;
    transition: width 0.3s ease;
}

.activityValue {
    color: #9c27b0;
    font-weight: 600;
    flex-shrink: 0;
    min-width: 40px;
    text-align: right;
}

.activityTimelineSection {
    background: white;
    padding: 15px;
    border-radius: 10px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    border: 1px solid #e8e8e8;
}

.activityTimelineSection h4 {
    margin: 0 0 15px 0;
    font-size: 0.95rem;
    color: #333;
}

.activityTimeline {
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-height: 300px;
    overflow-y: auto;
}

.activityTimelineBar {
    background: linear-gradient(90deg, #9c27b0, #ba68c8) !important;
}

/* ========================================
   Small (~ 480px)
   ======================================== */
@media (max-width: 480px) {
    .container {
        padding: 0 10px;
    }

    .header {
        margin-bottom: 15px;
    }

    .title {
        font-size: 1.3rem;
    }

    .subtitle {
        font-size: 0.8rem;
    }

    .filterBar {
        flex-direction: column;
        align-items: stretch;
        gap: 10px;
    }

    .userTypeFilter {
        width: 100%;
        justify-content: center;
    }

    .filterBtn {
        flex: 1;
        justify-content: center;
        padding: 8px 6px;
        font-size: 0.75rem;
    }

    .periodSelector {
        flex-direction: column;
        align-items: stretch;
    }

    .periodSelect {
        width: 100%;
    }

    .datePickerWrapper {
        width: 100%;
        flex-direction: column;
        align-items: stretch;
        gap: 8px;
    }

    .dateInputGroup {
        width: 100%;
    }

    .dateInput {
        flex: 1;
        width: 100%;
    }

    .dateSeparator {
        display: none;
    }

    .applyButton {
        width: 100%;
        padding: 10px;
    }

    .refreshButton {
        width: 100%;
    }

    .summary {
        grid-template-columns: repeat(2, 1fr);
        gap: 8px;
    }

    .summaryCard {
        padding: 12px;
    }

    .summaryCard h3 {
        font-size: 0.7rem;
        margin-bottom: 6px;
    }

    .summaryNumber {
        font-size: 1.2rem;
    }

    .statsCard,
    .statsCardWide {
        padding: 12px;
    }

    .statsCard h4,
    .statsCardWide h4 {
        font-size: 0.85rem;
        margin-bottom: 10px;
    }

    .statsItem {
        padding: 5px 6px;
        font-size: 0.75rem;
    }

    .timelineSection {
        padding: 12px;
    }

    .sectionTitle {
        font-size: 0.9rem;
    }

    .activitySection {
        margin-top: 20px;
        padding-top: 15px;
    }

    .activitySectionTitle {
        font-size: 1.1rem;
    }

    .activitySubtitle {
        font-size: 0.75rem;
    }

    .activitySummary {
        grid-template-columns: 1fr;
        gap: 8px;
    }

    .activitySummaryCard {
        padding: 12px;
    }

    .activitySummaryCard h3 {
        font-size: 0.7rem;
    }

    .activityNumber {
        font-size: 1.2rem;
    }

    .activityCard {
        padding: 12px;
    }

    .activityCard h4 {
        font-size: 0.85rem;
    }

    .activityItem {
        font-size: 0.75rem;
    }

    .activityTimelineSection {
        padding: 12px;
    }

    .activityTimelineSection h4 {
        font-size: 0.9rem;
    }
}
</style>
